import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';
import { Copy, Loader2, RefreshCcw, X } from 'lucide-react';

import { TagSelector } from './TagSelector';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useOrgDateFormat } from '@/lib/timezone';
import { Checkbox } from '@/components/ui/checkbox';
import { usePickerTemplates } from '@/features/templates/api/templates-api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  type ShareLinkDto,
  type ShareLinkExpiryHours,
  type ShareScope,
  useCreateShareLink,
  useRevokeShareLink,
} from '../api/patient-shares-api';

const DEFAULT_SCOPE: ShareScope = {
  flowsheet: null,
  encounters: true,
  pregnancies: true,
  demographics: true,
  document_tag_ids: [],
};

const EXPIRY_OPTIONS: { value: ShareLinkExpiryHours; label: string }[] = [
  { value: 1, label: '1 hour' },
  { value: 2, label: '2 hours' },
  { value: 5, label: '5 hours' },
  { value: 24, label: '24 hours' },
];

interface FormProps {
  canGenerate: boolean;
  expiry: ShareLinkExpiryHours;
  flowsheetTemplateId: string | null;
  loading: boolean;
  onGenerate: () => void;
  scope: ShareScope;
  setExpiry: (h: ShareLinkExpiryHours) => void;
  setFlowsheetTemplateId: (id: string | null) => void;
  setScope: (s: ShareScope) => void;
  templates: { id: string; name: string }[];
}

interface Props {
  existingLink?: ShareLinkDto | null;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  patientId: string;
}

export function ShareDialog({ open, patientId, onOpenChange, existingLink }: Props) {
  const [scope, setScope] = useState<ShareScope>(DEFAULT_SCOPE);
  const [expiry, setExpiry] = useState<ShareLinkExpiryHours>(2);
  const [flowsheetTemplateId, setFlowsheetTemplateId] = useState<string | null>(null);
  const [generated, setGenerated] = useState<ShareLinkDto | null>(existingLink ?? null);

  const create = useCreateShareLink(patientId);
  const revoke = useRevokeShareLink(patientId);

  const templatesQuery = usePickerTemplates({ category: 'context_view' });
  const templates = (templatesQuery.data ?? []).map((t) => ({
    id: t.id,
    name: t.name,
  }));

  useEffect(() => {
    if (open) {
      setGenerated(existingLink ?? null);
      if (!existingLink) {
        setScope(DEFAULT_SCOPE);
        setExpiry(2);
        setFlowsheetTemplateId(null);
      }
    }
  }, [open, existingLink]);

  const hasAnyScope =
    scope.demographics ||
    scope.encounters ||
    scope.pregnancies ||
    scope.flowsheet !== null ||
    (scope.document_tag_ids?.length ?? 0) > 0;

  const canGenerate = hasAnyScope && (scope.flowsheet === null || !!flowsheetTemplateId);

  const onGenerate = async () => {
    const body = {
      expires_in_hours: expiry,
      scope: {
        ...scope,
        flowsheet: scope.flowsheet ? { context_view_template_id: flowsheetTemplateId! } : null,
      },
    };
    const result = await create.mutateAsync(body);
    setGenerated(result);
  };

  const onRevoke = async () => {
    if (!generated) return;
    await revoke.mutateAsync(generated.id);
    setGenerated({ ...generated, is_revoked: true, is_active: false });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share patient chart</DialogTitle>
          <DialogDescription>Generate a read-only link for an external consulting provider. Generated reports include only signed versions available now; later amendments require a new link.</DialogDescription>
        </DialogHeader>

        {!generated ? (
          <FormState
            scope={scope}
            expiry={expiry}
            setScope={setScope}
            setExpiry={setExpiry}
            templates={templates}
            canGenerate={canGenerate}
            loading={create.isPending}
            flowsheetTemplateId={flowsheetTemplateId}
            setFlowsheetTemplateId={setFlowsheetTemplateId}
            onGenerate={onGenerate}
          />
        ) : (
          <GeneratedState
            link={generated}
            revoking={revoke.isPending}
            onRevoke={onRevoke}
            onReset={() => setGenerated(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormState(props: FormProps) {
  const {
    scope,
    expiry,
    loading,
    setScope,
    setExpiry,
    templates,
    onGenerate,
    canGenerate,
    flowsheetTemplateId,
    setFlowsheetTemplateId,
  } = props;

  const toggle = (key: 'demographics' | 'encounters' | 'pregnancies' | 'flowsheet') => (v: boolean) => {
    if (key === 'flowsheet') {
      setScope({
        ...scope,
        flowsheet: v ? { context_view_template_id: '' } : null,
      });
      if (!v) setFlowsheetTemplateId(null);
    } else {
      setScope({ ...scope, [key]: v });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-medium">What to share</Label>
        <div className="mt-2 space-y-2">
          <ScopeRow label="Demographics" checked={scope.demographics} onChange={toggle('demographics')} />
          <ScopeRow label="Encounters" checked={scope.encounters} onChange={toggle('encounters')} />
          <ScopeRow label="Pregnancies" checked={scope.pregnancies} onChange={toggle('pregnancies')} />
          <ScopeRow
            label="Flowsheet"
            disabled={templates.length === 0}
            checked={scope.flowsheet !== null}
            disabledHint={templates.length === 0 ? 'No context view templates configured' : undefined}
            onChange={toggle('flowsheet')}
          />
          {scope.flowsheet !== null && (
            <div className="pl-6">
              <Label className="text-xs text-muted-foreground">Context view template</Label>
              <Select value={flowsheetTemplateId ?? ''} onValueChange={(v) => setFlowsheetTemplateId(v || null)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      <div>
        <Label className="text-sm font-medium">Documents (by tag)</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          Pick tags to include matching documents. Leave empty to share none.
        </p>
        <div className="mt-2">
          <TagSelector
            placeholder="No documents shared"
            value={scope.document_tag_ids ?? []}
            onChange={(ids) => setScope({ ...scope, document_tag_ids: ids })}
          />
        </div>
      </div>

      <div>
        <Label className="text-sm font-medium">Expires in</Label>
        <Select value={String(expiry)} onValueChange={(v) => setExpiry(Number(v) as ShareLinkExpiryHours)}>
          <SelectTrigger className="mt-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EXPIRY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={String(o.value)}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DialogFooter>
        <Button disabled={!canGenerate || loading} onClick={onGenerate}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Generate link
        </Button>
      </DialogFooter>
    </div>
  );
}

function GeneratedState({
  link,
  onReset,
  onRevoke,
  revoking,
}: {
  link: ShareLinkDto;
  onRevoke: () => void;
  revoking: boolean;
  onReset: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [copied, setCopied] = useState(false);
  const { formatInstant } = useOrgDateFormat();

  useEffect(() => {
    if (!canvasRef.current || link.is_revoked) return;
    QRCode.toCanvas(canvasRef.current, link.share_url, { margin: 2, width: 240 });
  }, [link.share_url, link.is_revoked]);

  const copy = async () => {
    await navigator.clipboard.writeText(link.share_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-center">
        {link.is_revoked ? (
          <div className="flex h-60 w-60 items-center justify-center rounded border border-destructive/50 bg-destructive/5 text-destructive text-sm">
            <X className="mr-1 h-4 w-4" /> Revoked
          </div>
        ) : (
          <canvas ref={canvasRef} />
        )}
      </div>
      <div className="rounded bg-muted px-2 py-1 font-mono text-xs break-all">{link.share_url}</div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>Expires {formatInstant(link.expires_at, "MMM d, yyyy 'at' h:mm a")}</span>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={copy}>
            <Copy className="mr-1 h-3 w-3" /> {copied ? 'Copied' : 'Copy'}
          </Button>
          {!link.is_revoked && (
            <Button size="sm" variant="ghost" disabled={revoking} className="text-destructive" onClick={onRevoke}>
              {revoking ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCcw className="mr-1 h-3 w-3" />}
              Revoke
            </Button>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button size="sm" variant="outline" onClick={onReset}>
          Generate another
        </Button>
      </DialogFooter>
    </div>
  );
}

function ScopeRow({
  label,
  checked,
  onChange,
  disabled,
  disabledHint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  disabledHint?: string;
}) {
  return (
    <label title={disabledHint} className="flex items-center gap-2 text-sm">
      <Checkbox checked={checked} disabled={disabled} onCheckedChange={(v) => onChange(Boolean(v))} />
      <span className={disabled ? 'text-muted-foreground' : ''}>{label}</span>
    </label>
  );
}
